import { Component, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { environment } from '../../../environments/environment';
import { Logo } from '../../components/logo/logo';
import { UserService } from '../../services/user.service';

@Component({
	selector: 'app-login',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, RouterLink, Logo],
	templateUrl: './login.html',
	styleUrl: './login.css'
})
export class LoginComponent {
	loginForm: FormGroup;
	isSubmitting = signal(false);
	showError = signal(false);
	errorMessage = signal('');
	showPassword = signal(false);
	isAnimating = signal(false);
	private previousUrl: string | null = null;

	constructor(
		private fb: FormBuilder,
		private http: HttpClient,
		private router: Router,
		private userService: UserService,
		private location: Location
	) {
		this.loginForm = this.fb.group({
			email: ['', [Validators.required, Validators.email]],
			password: ['', [Validators.required, Validators.minLength(6)]],
			rememberMe: [false],
		});

		// Get the previous URL from navigation extras
		const navigation = this.router.getCurrentNavigation();
		this.previousUrl = navigation?.previousNavigation?.finalUrl?.toString() || null;

		// Check if user is already logged in
		const token = localStorage.getItem('authToken');
		if (token) {
			// If user details are already loaded, redirect based on role
			if (this.userService.userDetails()) {
				if (this.userService.isAdmin()) {
					this.router.navigate(['/admin/dashboard']);
				} else {
					this.router.navigate(['/']);
				}
			} else {
				// Fetch user info first
				this.userService.getUserInfo$().subscribe({
					next: () => {
						if (this.userService.isAdmin()) {
							this.router.navigate(['/admin/dashboard']);
						} else {
							this.router.navigate(['/']);
						}
					},
					error: () => {
						// If fetch fails, clear token and stay on login
						localStorage.removeItem('authToken');
					}
				});
			}
		}
	}

	togglePasswordVisibility() {
		this.showPassword.set(!this.showPassword());
	}

	onSubmit() {
		Object.keys(this.loginForm.controls).forEach((key) => {
			this.loginForm.get(key)?.markAsTouched();
		});

		if (this.loginForm.invalid) {
			return;
		}

		this.isSubmitting.set(true);
		const formData = this.loginForm.value;
		const apiUrl = `${environment.apiUrl}/login`;

		this.http.post(apiUrl, formData).subscribe({
			next: (response: any) => {
				this.isSubmitting.set(false);
				if (response.status) {
					// Store token
					if (response.data.token) {
						localStorage.setItem('authToken', response.data.token);
					}

					// Fetch user info to check role
					this.userService.getUserInfo$().subscribe({
						next: () => {
							// Redirect based on role
							if (this.userService.isAdmin()) {
								// Trigger animation before navigation
								this.animateAndNavigate('/admin/dashboard');
							} else {
								this.router.navigate(['/']);
							}
						},
						error: () => {
							// Fallback to home if user info fetch fails
							this.router.navigate(['/']);
						}
					});
				} else {
					const message =
						response.message || response.data || 'Login failed. Please try again.';
					this.errorMessage.set(message);
					this.showError.set(true);

					setTimeout(() => {
						this.showError.set(false);
					}, 5000);
				}
			},
			error: (error) => {
				console.error('Error during login:', error);
				this.isSubmitting.set(false);
				let message = 'An error occurred during login';
				if (error.error?.message) {
					message = error.error.message;
				}
				this.errorMessage.set(message);
				this.showError.set(true);

				setTimeout(() => {
					this.showError.set(false);
				}, 5000);
			},
		});
	}

	// Google Sign-In
	signInWithGoogle() {
		// Placeholder for Google OAuth implementation
		console.log('Google Sign-In clicked');
		// In production, integrate with Google OAuth
		// Example: this.authService.signInWithGoogle()
	}

	isFieldInvalid(fieldName: string): boolean {
		const field = this.loginForm.get(fieldName);
		return !!(field && field.invalid && field.touched);
	}

	getFieldError(fieldName: string): string {
		const field = this.loginForm.get(fieldName);

		if (field?.hasError('required')) {
			return `${this.getFieldLabel(fieldName)} is required`;
		}
		if (field?.hasError('email')) {
			return 'Please enter a valid email address';
		}
		if (field?.hasError('minlength')) {
			return 'Password must be at least 6 characters';
		}

		return '';
	}

	getFieldLabel(fieldName: string): string {
		const labels: { [key: string]: string } = {
			email: 'Email',
			password: 'Password',
		};
		return labels[fieldName] || fieldName;
	}

	animateAndNavigate(route: string) {
		this.isAnimating.set(true);

		// Navigate after animation completes
		setTimeout(() => {
			this.router.navigate([route]);
		}, 1500); // Wait for logo to reach sidebar position
	}

	goBack() {
		// Always navigate to homepage from login page
		// Using location.back() is unreliable as history may contain protected routes
		// that will just redirect back to login
		this.router.navigate(['/']);
	}
}
